import { makeStyles, shorthands, tokens } from "@fluentui/react-components";

export const CardStyles = makeStyles({
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: '20px',
    marginTop: '24px',
    marginBottom: '24px',
  },
  card: {
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid #e5e5e5',
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  title: { fontSize: '14px', fontWeight: 500 },
  valueText: { fontSize: '24px', fontWeight: 600, marginBottom: '4px' },
  subText: { color: '#666', fontSize: '13px' },
  icon: { color: '#666' },
});

export const DashboardLabelsTableStyles = makeStyles({
  solidField: {
    borderRadius: '6px',
    ':global(.fui-Input__input)': {
      backgroundColor: '#fff !important',
      border: '1px solid #e5e5e5 !important',
      borderRadius: '6px !important',
      boxShadow: 'none !important',
    },
    ':global(.fui-Dropdown__button)': {
      backgroundColor: '#fff !important',
      border: '1px solid #e5e5e5 !important',
      borderRadius: '6px !important',
      boxShadow: 'none !important',
    },
    ':global(.fui-Combobox__input)': {
      backgroundColor: '#fff !important',
      border: '1px solid #e5e5e5 !important',
      borderRadius: '6px !important',
      boxShadow: 'none !important',
    },
  },
});

export const DashboardLayoutStyles = makeStyles({
  pageWrap: {
    width: '100%',
    minHeight: '100dvh',
    boxSizing: 'border-box',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  surface: {
    width: '100%',
    minHeight: '100dvh',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground1,
    boxSizing: 'border-box',
    // Consistent padding with TenantDashboard
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalXL),
  },
  headingWrap: { display: 'grid', gap: tokens.spacingVerticalXS },
  headingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  headingTitle: {
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
    fontFamily: tokens.fontFamilyBase,
    fontSize: tokens.fontSizeHero700,
    lineHeight: tokens.lineHeightHero700,
    margin: 0,
  },
  headingSub: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  baseTemplateText: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    '& .base-template-label': {
      color: tokens.colorBrandForeground1,
      fontWeight: tokens.fontWeightSemibold,
    },
  },
  rightControls: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM },
  tabsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalXS,
    flexWrap: 'wrap',
  },
  tabWithIcon: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  // Add explicit tab styling for accessibility compliance
  accessibleTabList: {
    '& [role="tab"]': {
      backgroundColor: 'transparent !important',
      color: `${tokens.colorNeutralForeground1} !important`,
      cursor: 'pointer',
      position: 'relative',
      '&:focus': {
        outline: `2px solid ${tokens.colorBrandStroke1}`,
        outlineOffset: '2px',
        zIndex: 1,
      },
      '&:focus-visible': {
        outline: `2px solid ${tokens.colorBrandStroke1}`,
        outlineOffset: '2px',
        zIndex: 1,
      },
      '&[aria-selected="true"]': {
        backgroundColor: 'transparent !important',
        color: `${tokens.colorBrandForeground1} !important`,
        fontWeight: tokens.fontWeightSemibold,
      },
      '& span': {
        color: 'inherit !important',
        backgroundColor: 'transparent !important',
      },
    },
    // Ensure the TabList itself is focusable
    '&[role="tablist"]': {
      '&:focus-within': {
        outline: 'none',
      },
    },
  },
  // Ensure proper contrast for all text elements
  highContrastText: {
    color: `${tokens.colorNeutralForeground1} !important`,
    backgroundColor: 'transparent !important',
  },
  section: { width: '100%', overflowX: 'auto' },
});

export const DashboardPoliciesTableStyles = makeStyles({
  solidField: {
    borderRadius: '6px',
    ':global(.fui-Input__input)': {
      backgroundColor: '#fff !important',
      border: '1px solid #e5e5e5 !important',
      borderRadius: '6px !important',
      boxShadow: 'none !important',
    },
    ':global(.fui-Dropdown__button)': {
      backgroundColor: '#fff !important',
      border: '1px solid #e5e5e5 !important',
      borderRadius: '6px !important',
      boxShadow: 'none !important',
    },
    ':global(.fui-Combobox__input)': {
      backgroundColor: '#fff !important',
      border: '1px solid #e5e5e5 !important',
      borderRadius: '6px !important',
      boxShadow: 'none !important',
    },
  },
});

/** Shell-proof solid field styles */
export const DashboardSITTableStyles = makeStyles({
  solidField: {
    borderRadius: '6px',
    ':global(.fui-Input__input)': {
      backgroundColor: '#fff !important',
      border: '1px solid #e5e5e5 !important',
      borderRadius: '6px !important',
      boxShadow: 'none !important',
    },
    ':global(.fui-Dropdown__button)': {
      backgroundColor: '#fff !important',
      border: '1px solid #e5e5e5 !important',
      borderRadius: '6px !important',
      boxShadow: 'none !important',
    },
    ':global(.fui-Combobox__input)': {
      backgroundColor: '#fff !important',
      border: '1px solid #e5e5e5 !important',
      borderRadius: '6px !important',
      boxShadow: 'none !important',
    },
  },
});

/** Solid field styles (same as dashboard tables) */
export const TenantLabelTableStyles = makeStyles({
  solidField: {
    borderRadius: '6px',
    ':global(.fui-Input__input)': {
      backgroundColor: '#fff !important',
      border: '1px solid #e5e5e5 !important',
      borderRadius: '6px !important',
      boxShadow: 'none !important',
    },
    ':global(.fui-Dropdown__button)': {
      backgroundColor: '#fff !important',
      border: '1px solid #e5e5e5 !important',
      borderRadius: '6px !important',
      boxShadow: 'none !important',
    },
    ':global(.fui-Combobox__input)': {
      backgroundColor: '#fff !important',
      border: '1px solid #e5e5e5 !important',
      borderRadius: '6px !important',
      boxShadow: 'none !important',
    },
  },
});

export const TenantPolicyTableStyles = makeStyles({
  solidField: {
    borderRadius: '6px',
    ':global(.fui-Input__input)': {
      backgroundColor: '#fff !important',
      border: '1px solid #e5e5e5 !important',
      borderRadius: '6px !important',
      boxShadow: 'none !important',
    },
    ':global(.fui-Dropdown__button)': {
      backgroundColor: '#fff !important',
      border: '1px solid #e5e5e5 !important',
      borderRadius: '6px !important',
      boxShadow: 'none !important',
    },
  },
});

export const TenantKPICardsStyles = makeStyles({
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalM,
  },
  card: {
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid #e5e5e5',
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    // Make cards focusable and accessible
    cursor: 'pointer',
    position: 'relative',
    '&:focus': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
      border: `1px solid ${tokens.colorBrandStroke1}`,
    },
    '&:focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
      border: `1px solid ${tokens.colorBrandStroke1}`,
    },
    '&:hover': {
      border: `1px solid ${tokens.colorNeutralStroke1Hover}`,
      boxShadow: tokens.shadow4,
    },
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  title: { fontSize: '14px', fontWeight: 500 },
  valueText: { fontSize: '24px', fontWeight: 600, marginBottom: '4px' },
  subText: { color: '#666', fontSize: '13px' },
  icon: { color: '#666' },
  progressWrap: { marginTop: '12px' },
  progressTrack: { height: '2px', width: '100%', background: '#E5E5E5', borderRadius: '2px' },
  progressFill: { height: '2px', background: '#115EA3', borderRadius: '2px', marginTop: '-2px' },
});

export const TenantDashboardStyles = makeStyles({
  pageWrap: {
    width: '100%',
    minHeight: '100dvh',
    boxSizing: 'border-box',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  surface: {
    width: '100%',
    minHeight: '100dvh',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground1,
    boxSizing: 'border-box',
    // Consistent padding with DashboardLayout
    ...shorthands.padding(tokens.spacingVerticalXL, tokens.spacingHorizontalXL),
  },
  headingWrap: { display: 'grid', gap: tokens.spacingVerticalXS },
  headingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  headingTitle: {
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
    fontFamily: tokens.fontFamilyBase,
    fontSize: tokens.fontSizeHero700,
    lineHeight: tokens.lineHeightHero700,
    margin: 0,
  },
  rightControls: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginLeft: 'auto',
  },
  switchWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  tenantPicker: {
    minWidth: '220px',
    '@media (max-width: 768px)': { minWidth: '180px' },
  },
  headingSub: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  baseTemplateText: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    '& .base-template-label': {
      color: tokens.colorBrandForeground1,
      fontWeight: tokens.fontWeightSemibold,
    },
  },
  tabsAndToolbarRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalXS,
    flexWrap: 'wrap',
  },
  section: { width: '100%', overflowX: 'auto' },
  tabWithIcon: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  // Add explicit tab styling for accessibility compliance
  accessibleTabList: {
    '& [role="tab"]': {
      backgroundColor: 'transparent !important',
      color: `${tokens.colorNeutralForeground1} !important`,
      '&::before, &::after': {
        display: 'none !important',
      },
      '&[aria-selected="true"]': {
        backgroundColor: 'transparent !important',
        color: `${tokens.colorBrandForeground1} !important`,
      },
      '& span': {
        color: 'inherit !important',
        backgroundColor: 'transparent !important',
      },
    },
  },
  // Ensure proper contrast for all text elements
  highContrastText: {
    color: `${tokens.colorNeutralForeground1} !important`,
    backgroundColor: 'transparent !important',
  },
});

export const TenantSITStyles = makeStyles({
  solidField: {
    borderRadius: '6px',
    ':global(.fui-Input__input)': {
      backgroundColor: '#fff !important',
      border: '1px solid #e5e5e5 !important',
      borderRadius: '6px !important',
      boxShadow: 'none !important',
    },
    ':global(.fui-Dropdown__button)': {
      backgroundColor: '#fff !important',
      border: '1px solid #e5e5e5 !important',
      borderRadius: '6px !important',
      boxShadow: 'none !important',
    },
  },
});

/**
 * Grid Column Definitions for Dashboard Tables
 */
// Shared dashboard grid layout: Expand | Name | Category | Coverage | Last Refreshed | Actions
const DASHBOARD_GRID = '44px minmax(10vw, 25vw) minmax(8vw, 15vw) minmax(12vw, 20vw) minmax(7vw, 12vw) minmax(10vw, 14vw)';

export const GRID_COLUMNS = {
  DASHBOARD_SIT: DASHBOARD_GRID,
  DASHBOARD_LABELS: DASHBOARD_GRID,
  DASHBOARD_POLICIES: DASHBOARD_GRID,
} as const;

/**
 * Grid Column Definitions for Tenant Tables (single tenant view)
 * Uses viewport-relative units (vw) for better accessibility and responsive scaling
 */
export const TENANT_GRID_COLUMNS = {
  // Tenant SIT Table: Name | Confidence | Status | Last Deployed | Actions
  TENANT_SIT: 'minmax(12vw, 2.6fr) minmax(8vw, 1.2fr) minmax(10vw, 1.4fr) minmax(7vw, 1.3fr) minmax(9vw, 1.2fr)',

  // Tenant Labels Table: Name | Protection Level | Status | Last Deployed | Actions
  TENANT_LABELS: 'minmax(12vw, 2.6fr) minmax(10vw, 1.4fr) minmax(10vw, 1.4fr) minmax(7vw, 1.3fr) minmax(9vw, 1.2fr)',

  // Tenant Policies Table: Name | Priority | Status | Last Deployed | Actions
  TENANT_POLICIES: 'minmax(12vw, 2.6fr) minmax(8vw, 1.2fr) minmax(10vw, 1.4fr) minmax(7vw, 1.3fr) minmax(9vw, 1.2fr)',
} as const;